import { Injectable } from '@angular/core';

/**
 * Service responsible for managing authentication token in sessionStorage
 */
@Injectable({
  providedIn: 'root',
})
export class AuthStorageService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly TOKEN_EXPIRATION_KEY = 'auth_token_expiration';
  private readonly OAUTH_SESSION_KEY = 'oauth_session_id';
  /** JWT for POST /v1/login/passkeys/registration/* until a full session token exists */
  private readonly PASSKEY_SETUP_TOKEN_KEY = 'auth_passkey_setup_token';

  /**
   * Save authentication token to sessionStorage
   */
  saveToken(token: string): void {
    sessionStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Get authentication token from sessionStorage
   */
  getToken(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Remove authentication token from sessionStorage
   */
  removeToken(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
  }

  /**
   * Check if authentication token exists
   */
  hasToken(): boolean {
    return this.getToken() !== null;
  }

  /**
   * Save token expiration timestamp to sessionStorage
   */
  saveTokenExpiration(expirationTimestamp: number): void {
    sessionStorage.setItem(this.TOKEN_EXPIRATION_KEY, expirationTimestamp.toString());
  }

  /**
   * Get token expiration timestamp from sessionStorage
   */
  getTokenExpiration(): number | null {
    const expiration = sessionStorage.getItem(this.TOKEN_EXPIRATION_KEY);
    return expiration !== null ? parseInt(expiration, 10) : null;
  }

  /**
   * Remove token expiration timestamp from sessionStorage
   */
  removeTokenExpiration(): void {
    sessionStorage.removeItem(this.TOKEN_EXPIRATION_KEY);
  }

  /**
   * Check if token is expiring soon (within specified threshold in minutes)
   */
  isTokenExpiringSoon(thresholdMinutes: number): boolean {
    const expiration = this.getTokenExpiration();
    if (expiration === null) {
      return false;
    }

    const now = Date.now();
    const thresholdMs = thresholdMinutes * 60 * 1000;
    const timeUntilExpiration = expiration - now;

    return timeUntilExpiration <= thresholdMs && timeUntilExpiration > 0;
  }

  /**
   * Check if token is already expired
   */
  isTokenExpired(): boolean {
    const expiration = this.getTokenExpiration();
    if (expiration === null) {
      return false;
    }

    return Date.now() >= expiration;
  }

  /**
   * Save OAuth session ID to localStorage
   *
   * @param sessionId - The OAuth session ID to store
   */
  saveOAuthSessionId(sessionId: string): void {
    localStorage.setItem(this.OAUTH_SESSION_KEY, sessionId);
  }

  /**
   * Get OAuth session ID from localStorage
   *
   * @returns The stored OAuth session ID or null if not found
   */
  getOAuthSessionId(): string | null {
    return localStorage.getItem(this.OAUTH_SESSION_KEY);
  }

  /**
   * Remove OAuth session ID from localStorage
   */
  removeOAuthSessionId(): void {
    localStorage.removeItem(this.OAUTH_SESSION_KEY);
  }

  /**
   * Check if OAuth session ID exists
   *
   * @returns True if OAuth session ID is stored, false otherwise
   */
  hasOAuthSessionId(): boolean {
    return this.getOAuthSessionId() !== null;
  }

  savePasskeySetupToken(token: string): void {
    sessionStorage.setItem(this.PASSKEY_SETUP_TOKEN_KEY, token);
  }

  getPasskeySetupToken(): string | null {
    return sessionStorage.getItem(this.PASSKEY_SETUP_TOKEN_KEY);
  }

  removePasskeySetupToken(): void {
    sessionStorage.removeItem(this.PASSKEY_SETUP_TOKEN_KEY);
  }
}
